import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  useTheme,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { UserAdminProfile } from '@/types';
import { generateRandomPassword, tokens, useAuth } from '@/utilities';
import userAdminService from '@/api/userAdmin.service';
import Swal from 'sweetalert2';
import { collection, doc, updateDoc } from 'firebase/firestore';
import { firestore } from '@/config/firebase.config';

interface ResetPasswordModalProps {
  selectedAdmin: UserAdminProfile;
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
}

export const ResetPasswordModal = ({
  selectedAdmin,
  isModalOpen,
  setIsModalOpen,
}: ResetPasswordModalProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useAuth();
  
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: '',
    autoAssign: true,
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleClose = () => {
    setIsModalOpen(false);
    setPasswordData({
      password: '',
      confirmPassword: '',
      autoAssign: true,
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({ ...passwordData, autoAssign: e.target.checked });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
  };

  const handleResetPassword = async () => {
    Swal.fire({
      title: "¿Estás seguro?",
      text: "¿Deseas cambiar la contraseña de este usuario?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3F51B5",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, cambiar",
      cancelButtonText: "Cancelar"
    }).then(async (swalResult) => {
      if (swalResult.isConfirmed) {
        try {
          const idToken = await user?.user.getIdToken();
          if (!idToken) throw new Error("No se pudo obtener el token de autenticación");

          const newPassword = passwordData.autoAssign 
            ? generateRandomPassword(8) 
            : passwordData.password;

          const authUser = {
            uid: selectedAdmin.uid,
            email: selectedAdmin.email,
            password: newPassword,
            institution: selectedAdmin.institute || '',
          };

          const result = await userAdminService.changeUserPassword(authUser, idToken);

          if (result && !result.error) {
            // Also update mustChangePassword to true in Firestore so the user is forced to change it
            const collectionRef = collection(firestore, "users");
            const docRef = doc(collectionRef, selectedAdmin.uid);
            await updateDoc(docRef, { mustChangePassword: true });

            Swal.fire({
              title: "Contraseña actualizada",
              icon: "success",
              html: `
                <style>
                  .swal2-validation-message {
                    background-color: #f0fdf4 !important;
                    color: #166534 !important;
                    margin-top: 10px !important;
                  }
                  .swal2-validation-message::before {
                    background-color: #22c55e !important;
                  }
                </style>
                <div style="text-align: left; margin-top: 20px;">
                  <p><strong>Correo:</strong></p>
                  <div style="display: flex; align-items: center; gap: 10px; background: #f4f4f4; padding: 10px; border-radius: 5px; margin-bottom: 15px;">
                  <div style="flex-grow: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${selectedAdmin.email}</div>
                    <button id="copyEmailReset" class="swal2-confirm swal2-styled" style="margin: 0; padding: 5px 10px; font-size: 14px;">Copiar</button>
                  </div>
                  <p><strong>Nueva Contraseña:</strong></p>
                  <div style="display: flex; align-items: center; gap: 10px; background: #f4f4f4; padding: 10px; border-radius: 5px;">
                    <input type="password" id="swalPasswordReset" value="${newPassword}" readonly style="flex-grow: 1; border: none; background: transparent; font-size: 16px; width: 50%"/>
                    <button id="toggleSwalPswReset" class="swal2-confirm swal2-styled" style="margin: 0; padding: 5px 10px; font-size: 14px; background-color: #757575">Ver</button>
                    <button id="copyPswReset" class="swal2-confirm swal2-styled" style="margin: 0; padding: 5px 10px; font-size: 14px;">Copiar</button>
                  </div>
                  <p style="margin-top: 15px; font-size: 14px; color: #d32f2f;"><strong>Nota:</strong> Esta es una clave temporal. El usuario deberá cambiarla al iniciar sesión.</p>
                </div>
              `,
              confirmButtonText: "Cerrar",
              didOpen: () => {
                const copyEmailBtn = document.getElementById('copyEmailReset');
                const copyPswBtn = document.getElementById('copyPswReset');
                const togglePswBtn = document.getElementById('toggleSwalPswReset');
                const pswInput = document.getElementById('swalPasswordReset') as HTMLInputElement;

                copyEmailBtn?.addEventListener('click', () => {
                  navigator.clipboard.writeText(selectedAdmin.email);
                  Swal.showValidationMessage('Correo copiado');
                  setTimeout(() => Swal.resetValidationMessage(), 1500);
                });

                copyPswBtn?.addEventListener('click', () => {
                  navigator.clipboard.writeText(newPassword);
                  Swal.showValidationMessage('Contraseña copiada');
                  setTimeout(() => Swal.resetValidationMessage(), 1500);
                });

                togglePswBtn?.addEventListener('click', () => {
                  if (pswInput) {
                    if (pswInput.type === 'password') {
                      pswInput.type = 'text';
                      togglePswBtn.textContent = 'Ocultar';
                    } else {
                      pswInput.type = 'password';
                      togglePswBtn.textContent = 'Ver';
                    }
                  }
                });
              }
            });
            handleClose();
          } else {
            throw new Error(result.message || "Error al actualizar la contraseña");
          }
        } catch (error: any) {
          console.error(error);
          Swal.fire({
            title: "Error",
            text: error.message || "Hubo un problema al resetear la contraseña",
            icon: "error",
          });
        }
      }
    });
  };

  const isButtonDisabled = !passwordData.autoAssign && 
    (passwordData.password === '' || passwordData.password !== passwordData.confirmPassword);

  return (
    <Dialog open={isModalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: "#3F51B5", color: "white" }}>
        Resetear Contraseña - {selectedAdmin.displayName}
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={passwordData.autoAssign}
                onChange={handleCheckboxChange}
                sx={{ color: colors.blueAccent[500], '&.Mui-checked': { color: colors.blueAccent[500] } }}
              />
            }
            label={<Typography>Asignar contraseña automáticamente</Typography>}
          />

          {!passwordData.autoAssign && (
            <>
              <TextField
                required
                fullWidth
                label="Nueva Contraseña"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={passwordData.password}
                onChange={handleInputChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                required
                fullWidth
                label="Repetir Contraseña"
                name="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={handleInputChange}
                error={passwordData.password !== passwordData.confirmPassword && passwordData.confirmPassword !== ''}
                helperText={passwordData.password !== passwordData.confirmPassword && passwordData.confirmPassword !== '' ? 'Las contraseñas no coinciden' : ''}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleResetPassword}
          variant="contained"
          disabled={isButtonDisabled}
          sx={{ backgroundColor: "#3F51B5", color: "white", '&:hover': { backgroundColor: "#303F9F" } }}
        >
          Resetear Contraseña
        </Button>
      </DialogActions>
    </Dialog>
  );
};
